import React, { useEffect } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from "react-router-dom";
import { Authenticator, Spinner, UniversalAppShell } from "component-library";
import { UserTypes } from "shared-types";
import { useZendesk } from "shared-hooks";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import { UserClaimTypes, UserManager, UserRoles } from "app/core";

import { getLocalUrl } from "app/generics";
import { AddNewAudioPage, AddNewVideoPage } from "mediaModules/media";
import { CommonSection } from "sections/CommonSection";
import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import compose from "shared/utils/compose";
import { GlobalEventTypes } from "contexts/GlobalContext";
import { Logout } from "pages/AuthPages";
import {
  IRouteResolverProps,
  ResolveRouteAction,
  RouteResolver,
} from "components/common/RouteResolver";
import ProfileMiniFooter from "components/common/ProfileFooter";
import {
  AboutMePage,
  AddAgencyPage,
  AddNewAgencyPage,
  AgenciesHelpPage,
  AgenciesPage,
  AudioPage,
  BasicInfoPage,
  CreditsPage,
  EditAudioPage,
  EditCreditPage,
  EditVideoPage,
  GalleryPage,
  LinkToProfilePage,
  MainPage,
  PageNotFound,
  ProfileNotFoundPage,
  SkillsPage,
  SocialMediaAuthComplete,
  SocialMediaPage,
  TrainingPage,
  VideoPage,
} from "./PagesList";
import Profile from "pages/Profile";

const setCommonRouteRestrictions = (routes: IRouteResolverProps[]) => {
  routes.map((route) => {
    if (!route.rules) {
      route.rules = [];
    }

    if (!route.negativeRouteResolver) {
      route.negativeRouteResolver = getLocalUrl("404");
    }

    route.rules?.push({
      claim: UserClaimTypes.role,
      action: ResolveRouteAction.deny,
      values: [UserRoles.castingDirector],
    });

    return route;
  });
};

const setTechnicalRoutes = (
  routes: IRouteResolverProps[],
  basicPath: string
) => {
  routes.push(
    {
      path: `${basicPath}404`,
      component: PageNotFound,
    },
    {
      exact: true,
      path: `${basicPath}logout`,
      component: Logout,
    }
  );
};

const wrapInSuspense = (route: IRouteResolverProps): IRouteResolverProps => {
  if (route.component) {
    const Component = route.component;

    route.component = (props) => <Component {...props} />;
  }

  return route;
};

const renderRoutes = (basicPath: string, isProfileRedesignEnabled: boolean) => {
  const routes: IRouteResolverProps[] = [
    {
      exact: true,
      path: `${basicPath}`,
      component: isProfileRedesignEnabled ? Profile : MainPage,
    },
    {
      exact: true,
      path: `${basicPath}photos`,
      component: GalleryPage,
    },
    {
      exact: true,
      path: `${basicPath}credits`,
      component: CreditsPage,
    },
    {
      path: `${basicPath}credits/edit/:creditId?`,
      component: EditCreditPage,
    },
    {
      path: `${basicPath}credits/addcredit`,
      component: EditCreditPage,
    },
    {
      path: `${basicPath}details`,
      component: BasicInfoPage,
    },
    {
      path: `${basicPath}training`,
      component: TrainingPage,
    },
    {
      path: `${basicPath}aboutme`,
      component: AboutMePage,
    },
    {
      path: `${basicPath}skills`,
      component: SkillsPage,
    },
    {
      exact: true,
      path: `${basicPath}contacts`,
      component: AgenciesPage,
      rules: [
        {
          claim: UserClaimTypes.role,
          action: ResolveRouteAction.deny,
          values: [UserRoles.agent],
        },
      ],
    },
    {
      path: `${basicPath}contacts/add`,
      component: AddAgencyPage,
      rules: [
        {
          claim: UserClaimTypes.role,
          action: ResolveRouteAction.deny,
          values: [UserRoles.agent],
        },
      ],
    },
    {
      path: `${basicPath}contacts/new`,
      component: AddNewAgencyPage,
      rules: [
        {
          claim: UserClaimTypes.role,
          action: ResolveRouteAction.deny,
          values: [UserRoles.agent],
        },
      ],
    },
    {
      path: `${basicPath}contacts/help`,
      component: AgenciesHelpPage,
      rules: [
        {
          claim: UserClaimTypes.role,
          action: ResolveRouteAction.deny,
          values: [UserRoles.agent],
        },
        {
          claim: UserClaimTypes.artistType,
          action: ResolveRouteAction.allow,
          values: [
            UserTypes.youngPerformerMale,
            UserTypes.youngPerformerFemale,
          ],
        },
      ],
    },
    {
      path: `${basicPath}links`,
      component: SocialMediaPage,
      rules: [
        {
          claim: UserClaimTypes.role,
          action: ResolveRouteAction.allow,
          values: [UserRoles.spotlight],
        },
      ],
    },
    {
      path: `${basicPath}linktoprofile`,
      component: LinkToProfilePage,
    },
    {
      path: `${basicPath}media/video`,
      exact: true,
      component: VideoPage,
    },
    {
      path: `${basicPath}media/video/new`,
      component: AddNewVideoPage,
    },
    {
      path: `${basicPath}media/video/edit/:id`,
      component: EditVideoPage,
    },
    {
      path: `${basicPath}media/audio`,
      exact: true,
      component: AudioPage,
    },
    {
      path: `${basicPath}media/audio/new`,
      component: AddNewAudioPage,
    },
    {
      path: `${basicPath}media/audio/edit/:id`,
      component: EditAudioPage,
    },
  ];

  setCommonRouteRestrictions(routes);
  setTechnicalRoutes(routes, basicPath);

  const viewPin = UserManager.getClaim(UserClaimTypes.viewPin);
  routes.push({
    exact: true,
    path: `${basicPath}myviewpin`,
    rules: [
      {
        claim: UserClaimTypes.role,
        action: ResolveRouteAction.allow,
        values: [],
      },
    ],
    negativeRouteResolver: viewPin ? `/${viewPin}` : getLocalUrl("404"),
  });

  return (
    routes
      .map((route) => wrapInSuspense(route))
      // @ts-expect-error - type it
      .map((route, index) => <RouteResolver key={index} {...route} />)
  );
};

export interface IMasterPageProps
  extends RouteComponentProps,
    IGlobalContextProps {}

const MasterPage: React.FC<React.PropsWithChildren<IMasterPageProps>> = (
  props
) => {
  const isProfileRedesignEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.ProfileRedesign
  );
  const { match, globalContext, location } = props;
  const basicPath = `${match.path}profile/`;
  const viewPin = `${match.path}:viewPin(\\d{4}-\\d{4}-\\d{4})`;

  useZendesk(location.pathname);

  useEffect(() => {
    globalContext.notifyListener(
      GlobalEventTypes.makeVisibleGlobalSpinner,
      false
    );

    return () => {
      UserManager.setClaim(UserClaimTypes.userId, "");
    };
  }, []);

  const routes = React.useMemo(
    () => renderRoutes(basicPath, isProfileRedesignEnabled),
    [basicPath]
  );
  const isPublicProfilePage = matchPath(location.pathname, {
    exact: true,
    path: viewPin,
  });

  return (
    <UniversalAppShell
      footerComponent={isPublicProfilePage && <ProfileMiniFooter />}
      hideFooter={isProfileRedesignEnabled}
    >
      <div id="content-container">
        <div className="a-master-page__hidden-panel" />
        <React.Suspense fallback={<Spinner />}>
          <Switch>
            <Route
              path="/profile/social-connect/complete"
              exact
              component={SocialMediaAuthComplete}
            />
            <Route path="/profilenotfound" component={ProfileNotFoundPage} />
            <Redirect
              from={`${basicPath}:viewPin(\\d{4}-\\d{4}-\\d{4})`}
              to={viewPin}
            />
            <Route
              key="viewPin"
              exact={true}
              path={viewPin}
              component={isProfileRedesignEnabled ? Profile : MainPage}
            />
            <Authenticator>
              <Switch>{routes.map((route) => route)}</Switch>
            </Authenticator>
            <Redirect to={`${basicPath}404/`} />
          </Switch>
        </React.Suspense>
      </div>
      <CommonSection />
    </UniversalAppShell>
  );
};

export default compose(withGlobalContext, withRouter)(MasterPage);
